import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

interface ButtonLinkProps {
    href: string
    children: ReactNode
    variant?: 'primary' | 'outline'
    size?: 'md' | 'lg'
    external?: boolean
    className?: string
    onClick?: () => void
}

const VARIANTS = {
    primary: 'bg-race text-white shadow-[0_12px_40px_-14px_oklch(0.56_0.215_25/0.8)] hover:bg-race/85',
    outline: 'border border-white/20 text-foreground hover:border-white/50 hover:bg-white/5'
}

const SIZES = {
    md: 'h-11 px-6 text-sm',
    lg: 'h-12 px-7 text-[15px]'
}

const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    children,
    variant = 'primary',
    size = 'md',
    external = false,
    className,
    onClick
}): ReactNode => {
    const classes = cn(
        'inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        VARIANTS[variant],
        SIZES[size],
        className
    )

    if (external) {
        return (
            <a href={href} target='_blank' rel='noopener noreferrer' className={classes} onClick={onClick}>
                {children}
            </a>
        )
    }

    if (href.startsWith('#')) {
        return (
            <a href={href} className={classes} onClick={onClick}>
                {children}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} onClick={onClick}>
            {children}
        </Link>
    )
}

export default ButtonLink
